<template lang="html">
  <div class="sign-in">
    <div class="sign-in-view view">
      <h1>{{ pageTitle }}</h1>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <!-- Phase 1: Email Entry -->
      <div v-if="currentPhase === 'email'" class="email-phase">
        <form @submit.prevent="handleEmailSubmit" class="email-form">
          <div class="form-group">
            <label for="email" class="form-label">Email Address</label>
            <input
              id="email"
              v-model="email"
              type="email"
              placeholder="Enter your email address"
              required
              class="form-input"
              :disabled="loading"
            />
            <p class="form-help">We'll check which organizations you have access to</p>
          </div>

          <button
            type="submit"
            class="submit-btn email-submit-btn"
            :disabled="loading || !email.trim()"
          >
            {{ loading ? 'Checking...' : 'Continue' }}
          </button>
        </form>
      </div>

      <!-- Phase 2: Organization Selection -->
      <div v-if="currentPhase === 'organizations'" class="organizations-phase">
        <div class="org-info">
          <p class="org-description">{{ availableOrganizations.length > 1 ? 'Organizations found for' : 'Organization found for' }} {{ email }}</p>

          <input
            v-if="availableOrganizations.length > 1"
            v-model="orgSearchInput"
            type="text"
            class="form-input org-search"
            placeholder="Search organizations..."
            aria-label="Search organizations"
          />

          <div
            ref="orgListScroll"
            class="org-list-scroll"
            :class="{ 'org-list-scroll--fixed': availableOrganizations.length > 1 }"
          >
            <transition-group
              ref="orgListInner"
              name="org-fade"
              tag="div"
              class="org-list"
            >
              <div
                v-for="org in filteredOrganizations"
                :key="org.tenant"
                class="org-card selectable"
                :class="{ selected: selectedOrganization && selectedOrganization.tenant === org.tenant }"
                @click="selectOrganization(org)"
              >
                <div class="org-content">
                  <h3 class="org-name" :title="org.name">{{ org.name }}</h3>
                  <p class="org-domain" :title="org.tenant">{{ org.tenant }}</p>
                </div>
                <div v-if="selectedOrganization && selectedOrganization.tenant === org.tenant" class="selected-indicator">
                  ✓ Selected
                </div>
              </div>

              <p v-if="!filteredOrganizations.length" key="org-empty" class="org-empty">
                No organizations match "{{ orgSearch }}"
              </p>
            </transition-group>
          </div>
        </div>

        <div class="phase-actions">
          <button
            @click="handleBack"
            class="back-btn"
            :disabled="loading"
          >
            ← Back
          </button>

          <button
            @click="handleOrgSelect"
            class="submit-btn"
            :disabled="loading || !isOrgSelected"
          >
            {{ loading ? 'Preparing...' : 'Continue to Sign In' }}
          </button>
        </div>
      </div>

      <!-- Phase 3: Password Entry -->
      <div v-if="currentPhase === 'password'" class="password-phase">
        <form @submit.prevent="handlePasswordSubmit" class="password-form">
          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <input
              id="password"
              v-model="password"
              type="password"
              placeholder="Enter your password"
              required
              class="form-input"
              :disabled="loading"
            />
            <p class="form-help">Sign in to {{ selectedOrganization ? selectedOrganization.name : 'your organization' }}</p>
          </div>

          <div class="phase-actions">
            <button
              type="button"
              @click="handleBackToOrganizations"
              class="back-btn"
              :disabled="loading"
            >
              ← Back
            </button>

            <button
              type="submit"
              class="submit-btn"
              :disabled="loading || !password.trim()"
            >
              {{ loading ? 'Signing in...' : 'Sign In' }}
            </button>
          </div>
        </form>
      </div>

    </div>
    <!-- OTP Verification Modal -->
    <otp-verification-modal
      :visible="showOtpModal"
      :email="email"
      :loading="loading"
      :error="otpError"
      @verify="handleOtpVerify"
      @resend="handleOtpResend"
      @back="handleOtpBack"
      @clear-error="clearOtpError"
    />

    <!-- MFA Verification Modal -->
    <mfa-verification-modal
      :visible="showMfaModal"
      :loading="loading"
      :error="mfaError"
      @verify="handleMfaVerify"
      @back="handleMfaBack"
      @clear-error="clearMfaError"
    />

    <!-- MFA Setup Modal -->
    <mfa-setup-modal
      :visible="showMfaSetupModal"
      :qrcode-str="mfaSetupQrcode"
      :loading="loading"
      :error="mfaSetupError"
      @verify="handleMfaSetupVerify"
      @back="handleMfaSetupBack"
      @clear-error="clearMfaSetupError"
    />

    <!-- Email Verification Modal -->
    <email-verification-modal
      :visible="showEmailVerificationModal"
      :email="email"
      :loading="loading"
      :error="emailVerificationError"
      @verify="handleEmailVerificationVerify"
      @resend="handleEmailVerificationResend"
      @back="handleEmailVerificationBack"
      @clear-error="clearEmailVerificationError"
    />

    <modern-suggestion-with-action
      :suggestionText="$t('login.dont.have.account')"
      :actionText="$t('login.sign.up')"
      @clicked="goToRegistration"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import store from '@/store';
import { views } from '@/router/constants';
import {
  FIND_USER_POOL_ID,
  CLEAR_CHALLENGE,
  SET_AUTH,
  SET_PREFILL_DATA,
  SET_LOGIN_DOMAIN,
} from '@/store/auth/actionTypes';
import { CLEAN_INVITATION_CODE_DATA, REQUEST_EMAIL_VERIFICATION, VERIFY_EMAIL } from '@/store/registration/actionTypes';
import ModernSuggestionWithAction from '@/components/common/ModernSuggestionWithAction.vue';
import OtpVerificationModal from '@/components/popup/OtpVerificationModal.vue';
import MfaVerificationModal from '@/components/popup/MfaVerificationModal.vue';
import MfaSetupModal from '@/components/popup/MfaSetupModal.vue';
import EmailVerificationModal from '@/components/popup/EmailVerificationModal.vue';
import {
  findIdentityId, getUserLoginOptions, initiateOtpLogin, verifyOtpLogin,
} from '@/services/repository';
import {
  setConfig,
  getAuth,
  signIn,
  signOut,
  getSignInNextStepRoute,
  challengeSetupMFA,
  challengeVerifySetupMFACode,
} from '@/amplify/auth';
import { authErrors } from '@/amplify/errors';
import { Auth } from 'aws-amplify';

export default {
  name: 'ModernSignIn',
  components: {
    ModernSuggestionWithAction,
    OtpVerificationModal,
    MfaVerificationModal,
    MfaSetupModal,
    EmailVerificationModal,
  },
  data() {
    return {
      currentPhase: 'email', // 'email', 'organizations', 'password', 'otp'
      email: '',
      password: '',
      selectedOrganization: null,
      availableOrganizations: [],
      orgSearchInput: '',
      orgSearch: '',
      orgSearchTimer: null,
      loading: false,
      errorMessage: '',
      successMessage: '',
      showOtpModal: false,
      otpError: '',
      otpSession: null,
      showMfaModal: false,
      mfaError: '',
      mfaUser: null,
      showMfaSetupModal: false,
      mfaSetupError: '',
      mfaSetupQrcode: '',
      showEmailVerificationModal: false,
      emailVerificationError: '',
    };
  },
  computed: {
    ...mapGetters(['authChallenge', 'loginDomain', 'userPoolId']),
    pageTitle() {
      if (this.currentPhase === 'email') {
        return this.$t('login.title');
      }
      if (this.currentPhase === 'organizations') {
        return 'Choose Your Organization';
      }
      if (this.currentPhase === 'password') {
        return this.$t('login.title');
      }

      return this.$t('login.title');
    },
    isOrgSelected() {
      return this.selectedOrganization !== null;
    },
    filteredOrganizations() {
      // No search box below 2 orgs, so never let a stale query hide the list
      if (this.availableOrganizations.length <= 1) return this.availableOrganizations;
      const query = this.orgSearch.trim().toLowerCase();
      if (!query) return this.availableOrganizations;
      return this.availableOrganizations.filter(org => {
        const name = (org.name || '').toLowerCase();
        const tenant = (org.tenant || '').toLowerCase();
        return name.includes(query) || tenant.includes(query);
      });
    },
  },
  watch: {
    // Debounce the org search so the list animates fluidly instead of
    // re-filtering (and jumping) on every keystroke.
    orgSearchInput(value) {
      clearTimeout(this.orgSearchTimer);
      this.orgSearchTimer = setTimeout(() => {
        this.orgSearch = value;
      }, 150);
    },
    // Observe the list content only while the organization phase is on screen
    currentPhase(phase) {
      if (phase === 'organizations') {
        this.$nextTick(() => this.setupOrgListResize());
      } else {
        this.teardownOrgListResize();
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.orgSearchTimer);
    this.teardownOrgListResize();
  },
  async mounted() {
    // Check if there's an existing challenge before clearing
    const existingChallenge = this.$store.getters.authChallenge;
    const hasActiveChallenge = existingChallenge && existingChallenge.challengeName && existingChallenge.challengeUser;

    // Check if user is authenticated but email not verified (router guard redirect)
    try {
      const auth = await getAuth();
      if (auth && !auth.email_verified && !hasActiveChallenge) {
        // User was redirected here by router guard due to unverified email
        this.email = auth.email || '';
        this.errorMessage = 'Please verify your email address to continue.';

        // Request email verification code
        const domain = localStorage.getItem('domain') || '';
        await this.$store.dispatch(REQUEST_EMAIL_VERIFICATION, domain);

        // Show email verification modal
        this.showEmailVerificationModal = true;

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return;
      }
    } catch (error) {
      console.error('Error checking email verification status:', error);
    }

    if (hasActiveChallenge) {
      // User has an active challenge - show the appropriate modal
      const nextRouteName = getSignInNextStepRoute();

      if (nextRouteName === views.SIGN_IN_MFA_CODE) {
        this.mfaUser = existingChallenge.challengeUser;
        this.showMfaModal = true;
      } else if (nextRouteName === views.SIGN_IN_MFA_SETUP) {
        try {
          this.mfaSetupQrcode = await challengeSetupMFA();
          this.showMfaSetupModal = true;
        } catch (error) {
          console.error('MFA setup error:', error);
          this.errorMessage = 'Failed to initialize MFA setup. Please try again.';
          store.dispatch(CLEAR_CHALLENGE);
        }
      } else if (nextRouteName === views.SIGN_IN_CHANGE_PASSWORD) {
        // Redirect to password change page
        this.$router.push({ name: nextRouteName });
      }
    } else {
      // No active challenge - clear any stale challenge data
      store.dispatch(CLEAR_CHALLENGE);
    }

    store.dispatch(CLEAN_INVITATION_CODE_DATA);
    this.validateCurrentPhase();
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  },
  beforeUnmount() {
    // Restore body scroll when leaving
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  },
  methods: {
    ...mapActions([FIND_USER_POOL_ID, SET_AUTH, SET_PREFILL_DATA, CLEAR_CHALLENGE]),

    // Ease the scroll container's height to match its content (capped at ~3
    // rows) so the list shrinks smoothly after a row leaves, instead of snapping.
    setupOrgListResize() {
      this.teardownOrgListResize();
      const outer = this.$refs.orgListScroll;
      const groupRef = this.$refs.orgListInner;
      const inner = groupRef && (groupRef.$el || groupRef);
      if (!outer || !inner || typeof ResizeObserver === 'undefined') return;
      const capPx = 216;
      const applyHeight = animate => {
        const target = Math.min(inner.offsetHeight, capPx);
        outer.style.transition = animate
          ? 'height 0.4s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none';
        outer.style.height = `${target}px`;
      };
      applyHeight(false);
      this.orgListResizeObserver = new ResizeObserver(() => applyHeight(true));
      this.orgListResizeObserver.observe(inner);
    },
    teardownOrgListResize() {
      if (this.orgListResizeObserver) {
        this.orgListResizeObserver.disconnect();
        this.orgListResizeObserver = null;
      }
    },

    async handleEmailSubmit() {
      if (!this.email.trim()) return;

      this.loading = true;
      this.clearMessages();

      try {
        // Extract domain from email
        const [, emailDomain] = this.email.split('@');

        if (!emailDomain) {
          throw new Error('Please enter a valid email address');
        }

        // Get all organizations for the user from the backend
        // The backend will determine auth flow capabilities
        this.availableOrganizations = await this.getUserOrganizations(this.email);

        if (this.availableOrganizations.length === 0) {
          this.errorMessage = 'No organizations found for this email address. Please contact your administrator.';
          return;
        }

        if (this.availableOrganizations.length === 0) {
          this.errorMessage = 'No organizations found for this email address. Please contact your administrator.';
          return;
        }

        // Always show organization selection phase (reset any prior search)
        this.orgSearchInput = '';
        this.orgSearch = '';
        this.currentPhase = 'organizations';
      } catch (error) {
        console.error('Email validation error:', error);
        if (error.message.includes('No organizations found')) {
          this.errorMessage = error.message;
        } else if (error.message.includes('domain does not exist')) {
          this.errorMessage = 'No organization found for this email domain. Please contact your administrator.';
        } else {
          this.errorMessage = error.message || 'Unable to validate your email. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },

    async handleOrgSelect() {
      if (!this.selectedOrganization) return;

      this.clearMessages();

      try {
        // Check if selected organization requires classic flow (password)
        if (!this.selectedOrganization.otp_enabled) {
          // Move to password phase
          this.currentPhase = 'password';
          return;
        }

        // Modern flow - continue with OTP
        this.loading = true;
        // Set up the user pool for this domain (if not already done)
        if (!this.userPoolId) {
          await this.$store.dispatch(FIND_USER_POOL_ID, { domain: this.selectedOrganization.tenant });
        }

        // Initiate OTP login process
        await this.initiateOtpLogin();

        // Show OTP modal
        this.showOtpModal = true;
      } catch (error) {
        console.error('Organization selection error:', error);
        this.errorMessage = 'Unable to prepare sign-in. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async handlePasswordSubmit() {
      if (!this.password.trim() || !this.selectedOrganization) return;

      this.loading = true;
      this.clearMessages();

      try {
        // Use the same logic as old SignIn.vue
        await this.$store.dispatch(SET_LOGIN_DOMAIN, this.selectedOrganization.tenant);
        localStorage.setItem('domain', this.selectedOrganization.tenant);

        // Call signIn which handles all the auth logic internally
        await signIn(this.email.trim(), this.password, this.selectedOrganization.tenant.trim());

        // If we get here, login was successful - check for email verification
        const auth = await getAuth(true);
        if (auth && !auth.email_verified) {
          // Request email verification code
          const domainOfRegistration = this.selectedOrganization.tenant || '';
          await this.$store.dispatch(REQUEST_EMAIL_VERIFICATION, domainOfRegistration);
          // Show email verification modal - DO NOT redirect yet
          this.showEmailVerificationModal = true;
          this.loading = false;
          return;
        }

        // Only redirect if email is verified
        this.successMessage = 'Sign in successful! Redirecting...';
        setTimeout(() => {
          this.$router.push({ name: views.DASHBOARD });
        }, 500);
      } catch (error) {
        console.error('Password login error:', error);
        this.loading = false;
        const { code, message } = error || {};

        // Check for 403 Forbidden (platform access denied)
        if (error.response?.status === 403) {
          this.errorMessage = error.response?.data?.message || 'Platform access is not enabled for your organization. Please contact support.';
          return;
        }

        if (code === authErrors.NOT_AUTHORIZED) {
          this.errorMessage = message || 'Invalid password. Please try again.';
        } else if (code === authErrors.CHALLENGE_REQUIRED) {
          // Handle MFA/TOTP challenges
          const nextRouteName = getSignInNextStepRoute();

          if (nextRouteName === views.SIGN_IN_MFA_CODE) {
            // Show MFA verification modal
            const challenge = this.$store.getters.authChallenge;
            this.mfaUser = challenge.challengeUser;
            this.showMfaModal = true;
            this.loading = false;
            return;
          }

          if (nextRouteName === views.SIGN_IN_MFA_SETUP) {
            // Show MFA setup modal
            try {
              this.mfaSetupQrcode = await challengeSetupMFA();
              this.showMfaSetupModal = true;
              this.loading = false;
              return;
            } catch (setupError) {
              console.error('MFA setup error:', setupError);
              this.errorMessage = 'Failed to initialize MFA setup. Please try again.';
            }
          }

          if (nextRouteName === views.SIGN_IN_CHANGE_PASSWORD) {
            // For password change, still redirect (could be converted to modal later)
            this.$router.push({ name: nextRouteName });
          } else if (!nextRouteName) {
            this.errorMessage = this.$t('error.general');
          }
        } else {
          this.errorMessage = message || this.$t('error.general');
        }
        this.loading = false;
      }
    },

    async handleMfaVerify(mfaCode) {
      this.loading = true;
      this.clearMfaError();

      try {
        // Verify MFA code
        const user = await Auth.confirmSignIn(this.mfaUser, mfaCode, 'SOFTWARE_TOKEN_MFA');

        if (user && user.signInUserSession && user.signInUserSession.idToken) {
          const idTokenPayload = user.signInUserSession.idToken.payload;
          await this.$store.dispatch(SET_AUTH, idTokenPayload);
          this.showMfaModal = false;

          // Check if email verification is required - DO NOT redirect yet
          if (!idTokenPayload.email_verified) {
            // Request email verification code
            const domainOfRegistration = this.selectedOrganization?.tenant || '';
            await this.$store.dispatch(REQUEST_EMAIL_VERIFICATION, domainOfRegistration);

            // Show email verification modal - stay on this page
            this.showEmailVerificationModal = true;
            this.loading = false;
            return;
          }

          // Only redirect if email is verified
          this.successMessage = 'Sign in successful! Redirecting...';
          setTimeout(() => {
            this.$router.push({ name: views.DASHBOARD });
          }, 500);
        } else {
          this.loading = false;
          throw new Error('MFA verification failed');
        }
      } catch (error) {
        console.error('MFA verification failed:', error);
        this.loading = false;
        this.mfaError = error.message || 'Invalid verification code. Please try again.';
      }
    },

    async handleMfaBack() {
      this.showMfaModal = false;
      this.clearMfaError();
      this.mfaUser = null;
      // Clear the challenge state and sign out
      await this.$store.dispatch(CLEAR_CHALLENGE);
      await signOut();
      // Reset to email phase
      this.currentPhase = 'email';
      this.password = '';
      this.clearMessages();
    },

    clearMfaError() {
      this.mfaError = '';
    },

    async handleMfaSetupVerify(mfaCode) {
      this.loading = true;
      this.clearMfaSetupError();

      try {
        await challengeVerifySetupMFACode(mfaCode);

        // Get updated auth
        const auth = await getAuth(true);
        await this.$store.dispatch(SET_AUTH, auth);

        this.showMfaSetupModal = false;

        // Check if email verification is required - DO NOT redirect yet
        if (auth.email_verified === false || auth.email_verified === 'false') {
          // Request email verification code
          const domainOfRegistration = this.selectedOrganization?.tenant || '';
          await this.$store.dispatch(REQUEST_EMAIL_VERIFICATION, domainOfRegistration);

          // Show email verification modal - stay on this page
          this.showEmailVerificationModal = true;
          this.loading = false;
          return;
        }

        // Only redirect if email is verified
        this.successMessage = 'MFA setup complete! Redirecting...';
        setTimeout(() => {
          this.$router.push({ name: views.DASHBOARD });
        }, 500);
      } catch (error) {
        console.error('MFA setup verification failed:', error);
        this.loading = false;
        const { code } = error || {};
        if (code === authErrors.SETUP_MFA) {
          this.mfaSetupError = this.$t('error.incorrectVerificationCode');
        } else {
          this.mfaSetupError = error.message || 'Invalid verification code. Please try again.';
        }
      }
    },

    async handleMfaSetupBack() {
      this.showMfaSetupModal = false;
      this.clearMfaSetupError();
      this.mfaSetupQrcode = '';
      // Clear the challenge state and sign out
      await this.$store.dispatch(CLEAR_CHALLENGE);
      await signOut();
      // Reset to email phase
      this.currentPhase = 'email';
      this.password = '';
      this.clearMessages();
    },

    clearMfaSetupError() {
      this.mfaSetupError = '';
    },

    async handleEmailVerificationVerify(code) {
      this.loading = true;
      this.clearEmailVerificationError();

      try {
        // Verify email with code
        await this.$store.dispatch(VERIFY_EMAIL, { code });

        // Get updated auth with verified email
        const auth = await getAuth(true);
        await this.$store.dispatch(SET_AUTH, auth);

        this.showEmailVerificationModal = false;
        // Email is now verified - this is the ONLY place we redirect after email verification
        this.successMessage = 'Email verified successfully! Redirecting...';
        setTimeout(() => {
          this.$router.push({ name: views.DASHBOARD });
        }, 500);
      } catch (error) {
        console.error('Email verification failed:', error);
        this.loading = false;
        this.emailVerificationError = error?.response?.data?.message || error.message || 'Invalid verification code. Please try again.';
      }
    },

    async handleEmailVerificationResend() {
      try {
        const domainOfRegistration = this.selectedOrganization?.tenant || '';
        await this.$store.dispatch(REQUEST_EMAIL_VERIFICATION, domainOfRegistration);
      } catch (error) {
        this.emailVerificationError = 'Failed to resend code. Please try again.';
      }
    },

    handleEmailVerificationBack() {
      this.showEmailVerificationModal = false;
      this.clearEmailVerificationError();
    },

    clearEmailVerificationError() {
      this.emailVerificationError = '';
    },

    handleBack() {
      this.currentPhase = 'email';
      this.selectedOrganization = null;
      this.password = '';
      this.orgSearchInput = '';
      this.orgSearch = '';
      this.clearMessages();
    },

    handleBackToOrganizations() {
      this.currentPhase = 'organizations';
      this.password = '';
      this.clearMessages();
    },

    clearMessages() {
      this.errorMessage = '';
      this.successMessage = '';
    },

    selectOrganization(org) {
      // Select the clicked organization
      this.selectedOrganization = org;
      this.clearMessages();
    },

    goToRegistration() {
      this.$router.push({ name: views.SIGN_UP });
    },

    async getUserOrganizations(email) {
      try {
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout - please try again')), 30000));
        const apiPromise = getUserLoginOptions(email);
        const response = await Promise.race([apiPromise, timeoutPromise]);
        if (response.success && response.tenants && response.tenants.length > 0) {
          const tenants = response.tenants.map(tenant => ({
            tenant: tenant.tenant,
            name: this.formatTenantName(tenant.tenant),
            auth_flow_hint: tenant.auth_flow_hint || 'custom_auth',
            otp_enabled: tenant.otp_enabled || false,
            custom_auth_available: tenant.otp_enabled || false,
            userPoolId: tenant.user_pool_id,
            pool_name: tenant.pool_name,
            username: tenant.username,
          }));
          return tenants.sort((a, b) => a.tenant.localeCompare(b.tenant, undefined, { sensitivity: 'base' }));
        }
        // If no organizations found in API response, show error
        throw new Error(response.message || 'No organizations found for this email address');
      } catch (error) {
        console.error('Error fetching user organizations:', error);
        throw error; // Re-throw to be handled by the calling method
      }
    },

    formatTenantName(tenant) {
      // Convert tenant domain to a readable name
      if (!tenant) return 'Organization';
      // Special case for cynergy.app - keep it as is
      if (tenant === 'cynergy.app') {
        return 'cynergy.app';
      }
      // Remove common prefixes and suffixes for other domains
      const name = tenant
        .replace(/^(www\.|dev\.|staging\.|prod\.)/, '')
        .replace(/\.(com|org|net)$/, '');
      // Capitalize first letter and replace dots/hyphens with spaces
      return name
        .split(/[.-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    async initiateOtpLogin() {
      try {
        const response = await initiateOtpLogin(this.email, this.selectedOrganization.tenant);
        if (response.success) {
          this.otpSession = response.session;
          // Store the userPoolId and clientId for later use
          this.selectedOrganization.userPoolId = response.userPoolId;
          this.selectedOrganization.clientId = response.clientId;
          this.selectedOrganization.identityPoolId = await findIdentityId({userPoolId: this.selectedOrganization.userPoolId});
          this.loading = false;
        } else {
          throw new Error(response.error || 'Failed to initiate OTP');
        }
      } catch (error) {
        console.error('Failed to initiate OTP:', error);
        throw error;
      }
    },

    async handleOtpVerify(otpCode) {
      this.loading = true;
      this.clearOtpError();

      try {
        const response = await verifyOtpLogin(otpCode, this.otpSession, this.email, this.selectedOrganization.tenant, this.email);

        if (response.success) {
          try {
            await this.setupAuthenticationState(response.tokens);
          } catch (authError) {
            console.error('Error setting up auth state:', authError);
          }
          this.showOtpModal = false;
          // OTP login already verifies email ownership - safe to redirect
          this.successMessage = 'Sign in successful! Redirecting...';
          setTimeout(() => {
            this.$router.push({ name: views.DASHBOARD });
          }, 500);
        } else {
          this.loading = false;
          throw new Error(response.error || 'OTP verification failed');
        }
      } catch (error) {
        console.error('OTP verification failed:', error);
        this.loading = false;
        this.otpError = error.message || 'Invalid verification code. Please try again.';
      }
    },

    async handleOtpResend() {
      try {
        await this.initiateOtpLogin();
      } catch (error) {
        this.otpError = 'Failed to resend code. Please try again.';
      }
    },

    handleOtpBack() {
      this.showOtpModal = false;
      this.clearOtpError();
      this.otpSession = null;
    },

    clearOtpError() {
      this.otpError = '';
    },

    parseJwtPayload(token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
        return JSON.parse(jsonPayload);
      } catch (error) {
        console.error('Failed to parse JWT token:', error);
        return {};
      }
    },

    validateCurrentPhase() {
      if (this.currentPhase === 'organizations' && !this.email) {
        this.currentPhase = 'email';
      }
    },

    async setupAuthenticationState(tokens) {
      try {
        if (this.selectedOrganization.userPoolId) {
          localStorage.setItem('userPoolId', this.selectedOrganization.userPoolId);
          localStorage.setItem('webClientId', this.selectedOrganization.clientId);
          localStorage.setItem('domain', this.selectedOrganization.tenant);
          localStorage.setItem('identityId', this.selectedOrganization.identityPoolId || '');
          localStorage.setItem('bucketName', this.selectedOrganization.userPoolId.replace(/_/g, '').toLowerCase());
        }
        // Step 2: Create Amplify session in localStorage
        const idTokenPayload = this.parseJwtPayload(tokens.idToken);
        const { clientId } = this.selectedOrganization;
        const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
        const username = idTokenPayload.email || idTokenPayload['cognito:username'];
        localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
        localStorage.setItem(`${keyPrefix}.${username}.idToken`, tokens.idToken);
        localStorage.setItem(`${keyPrefix}.${username}.accessToken`, tokens.accessToken);
        localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, tokens.refreshToken);
        localStorage.setItem(`${keyPrefix}.${username}.clockDrift`, '0');
        // Step 3: Configure Amplify with the new settings
        setConfig();
        // Step 4: ONLY NOW dispatch SET_AUTH (which will trigger API calls with proper auth)
        await this.$store.dispatch(SET_AUTH, idTokenPayload);
      } catch (error) {
        console.error('Error setting up authentication state:', error);
        throw error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.sign-in {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-end;
  background: transparent !important; /* Ensure layout background shows through */
  overflow: hidden;
  padding: rem(20);
  box-sizing: border-box;
}

.modern-suggestion {
  position: fixed;
  bottom: rem(30);
  right: rem(40);
  z-index: 10;
}

.sign-in-view {
  width: rem(544);
  max-width: rem(544);
  min-width: rem(544);
  padding: $space-8;
  margin-right: $space-10;
  margin-top: 0;
  background: $auth-card-bg;
  backdrop-filter: blur(rem(20));
  border-radius: $radius-2xl;
  // Layered elevation so the card reads as clearly raised (3D)
  box-shadow: 0 rem(8) rem(20) $auth-card-shadow,
    0 rem(24) rem(50) $auth-card-shadow;
  border: rem(1) solid $auth-card-border;
  flex-shrink: 0;
  max-height: calc(100vh - rem(140));
  overflow-y: auto;
  box-sizing: border-box;
}

h1 {
  text-align: center;
  font-size: $font-size-3xl;
  line-height: rem(28);
  font-weight: 700;
  color: $auth-text-primary;
  margin-bottom: $space-4;
  margin-top: 0;
}

.error-message {
  color: $auth-error;
  font-size: rem(13);
  line-height: rem(18);
  min-height: rem(18);
  margin-bottom: $space-4;
  margin-top: 0;
  text-align: center;
  padding: $space-3 $space-4;
  background: $auth-error-bg;
  border-radius: $radius-xl;
  border: rem(1) solid $auth-error-border;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.success-message {
  color: $auth-success;
  font-size: rem(13);
  line-height: rem(18);
  min-height: rem(18);
  margin-bottom: $space-4;
  margin-top: 0;
  text-align: center;
  padding: $space-3 $space-4;
  background: $auth-success-bg;
  border-radius: $radius-xl;
  border: rem(1) solid $auth-success-border;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

// Email Phase Styles
.email-phase {
  width: 100%;
}

.email-form {
  display: flex;
  flex-direction: column;
  gap: $space-6;
}

// Password Phase Styles
.password-phase {
  width: 100%;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: $space-6;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  width: 100%;
}

.form-label {
  font-size: $font-size-md;
  font-weight: 600;
  color: $auth-text-primary;
  margin-bottom: $space-1;
}

.form-input {
  width: 100%;
  padding: $space-4 $space-5;
  border: rem(2) solid $auth-border;
  border-radius: $radius-2xl;
  font-size: $font-size-md;
  background: $auth-bg-primary;
  transition: all 0.3s ease;
  &:focus {
    outline: none;
    border-color: $auth-brand;
  }
  &:disabled {
    background-color: $auth-bg-secondary;
    cursor: not-allowed;
    opacity: 0.7;
  }
  &::placeholder {
    color: $auth-text-muted;
  }
}

.form-help {
  font-size: $font-size-base;
  color: $auth-text-secondary;
  margin: 0;
  font-weight: 500;
  text-align: center;
}

.submit-btn {
  flex: 0 1 auto;
  min-width: rem(128);
  padding: $space-2 $space-6;
  background: linear-gradient(135deg, $auth-gradient-start 0%, $auth-gradient-end 100%);
  color: $auth-bg-primary !important;
  border: none;
  border-radius: $radius-lg;
  font-size: $font-size-base;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 rem(3) rem(8) $auth-brand-shadow;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, $auth-brand-hover 0%, #9333ea 100%);
    color: $auth-bg-primary !important;
    box-shadow: 0 rem(4) rem(12) $auth-brand-shadow-hover;
  }
  &:disabled {
    background: $auth-border;
    color: $auth-text-muted !important;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.email-submit-btn {
  align-self: center;
}

// Organizations Phase Styles
.organizations-phase {
  width: 100%;
}

.org-info {
  margin-bottom: $space-5;
}

.org-description {
  font-size: $font-size-md;
  color: $auth-text-secondary;
  text-align: center;
  margin-bottom: $space-6;
  font-weight: 500;
}

.org-search {
  display: block;
  width: 80%;
  margin: 0 auto $space-4;
  padding: $space-2 $space-4;
  font-size: $font-size-base;
}

.org-empty {
  font-size: $font-size-base;
  color: $auth-text-muted;
  text-align: center;
  padding: $space-4 0;
  margin: 0;
  word-break: break-word;
}

// Fluid list animation for the filtered organization results
.org-fade-enter-active {
  transition: opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
// Leaving rows fade out sliding left-to-right, with a small delay so it is not
// sudden. Kept in flow (not absolute) so the row holds its slot while it exits;
// the container only collapses afterwards, via the move transition below.
.org-fade-leave-active {
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.06s,
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.06s;
}
.org-fade-enter {
  opacity: 0;
  transform: translateY(rem(-6));
}
.org-fade-leave-to {
  opacity: 0;
  transform: translateX(rem(32));
}
.org-fade-move {
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

// Outer scroll frame: caps the height and scrolls. Its height is eased in JS
// (setupOrgListResize) so the list shrinks smoothly after a row leaves.
// overflow-x hidden so the left-to-right row exit never shows a horizontal bar.
.org-list-scroll {
  max-height: rem(400);
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: $space-6;
  &--fixed {
    max-height: rem(216);
  }
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: rem(6);
  }
  &::-webkit-scrollbar-track {
    background: $auth-bg-tertiary;
    border-radius: rem(3);
  }
  &::-webkit-scrollbar-thumb {
    background: $auth-border-hover;
    border-radius: rem(3);
    &:hover {
      background: $auth-text-muted;
    }
  }
}

// Inner content: natural height (measured by the ResizeObserver).
.org-list {
  position: relative;
  // Breathing room so card shadows are not clipped, esp. the bottom one
  padding: $space-2 $space-2 $space-3;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.org-card {
  width: 80%;
  border: rem(2) solid $auth-border;
  border-radius: $radius-xl;
  padding: $space-1 $space-3;
  background: linear-gradient(135deg, $auth-bg-primary 0%, $auth-bg-secondary 100%);
  position: relative;
  transition: all 0.3s ease;
  margin-bottom: $space-2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 rem(3) rem(10) $auth-card-shadow;
  &:last-child {
    margin-bottom: 0;
  }
  &.selectable {
    cursor: pointer;
    &:hover {
      border-color: $auth-brand-border;
      background: linear-gradient(135deg, $auth-bg-primary 0%, $auth-brand-lighter 100%);
      box-shadow: 0 rem(6) rem(18) $auth-brand-shadow;
      transform: translateY(rem(-1));
    }
  }
  &.selected {
    border-color: $auth-brand;
    background: linear-gradient(135deg, $auth-brand-lighter 0%, $auth-brand-light 100%);
    box-shadow: 0 rem(5) rem(8) rgba(143, 36, 208, 0.3);
  }
}

.org-content {
  flex: 1;
  min-width: 0; // allow the name/domain to truncate instead of overflowing
  padding-right: rem(70); // Make room for selected indicator
}

.org-name {
  font-size: rem(15);
  font-weight: 600;
  color: $auth-text-primary;
  margin: 0;
  margin-bottom: $space-0-5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-domain {
  font-size: rem(13);
  color: $auth-text-secondary;
  margin: 0;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-status {
  margin-top: $space-2;
}

.auth-type {
  font-size: $font-size-base;
  padding: $space-2 $space-4;
  border-radius: $radius-xl;
  font-weight: 600;
  display: inline-block;
  &.modern {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.2) 100%);
    color: $auth-success;
    border: rem(1) solid rgba(34, 197, 94, 0.3);
  }
  &.classic {
    background: linear-gradient(135deg, rgba(100, 116, 139, 0.1) 0%, rgba(100, 116, 139, 0.2) 100%);
    color: $auth-text-secondary;
    border: rem(1) solid rgba(100, 116, 139, 0.3);
  }
}

.selected-indicator {
  flex-shrink: 0;
  font-size: $font-size-sm;
  color: $auth-brand;
  font-weight: 700;
  background: $auth-brand-light;
  padding: $space-1 $space-2;
  border-radius: $radius-lg;
  border: rem(1) solid $auth-brand-shadow;
}

.phase-actions {
  display: flex;
  gap: $space-3;
  margin-top: $space-2;
  justify-content: center;
  align-items: center;
}

.back-btn {
  flex: 0 1 auto;
  min-width: rem(128);
  padding: $space-2 $space-6;
  background: linear-gradient(135deg, $auth-bg-primary 0%, $auth-bg-secondary 100%);
  color: $auth-text-secondary;
  border: rem(2) solid $auth-border;
  border-radius: $radius-lg;
  font-size: $font-size-base;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, $auth-bg-secondary 0%, $auth-bg-tertiary 100%);
    border-color: $auth-border-hover;
    box-shadow: 0 rem(2) rem(8) $auth-card-shadow;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
}
</style>
